import { useEffect, useMemo, useState } from 'react';
import type { FieldProps } from './TextField';
import type { QuestionOption } from '../../types';
import { useFormField } from '../../hooks/useFormField';
import { FieldsetWrapper } from '../QuestionWrapper';

function normalizeOptions(options: QuestionOption[] = []): QuestionOption[] {
  return options.map((option) => ({
    ...option,
    label: option.label || option.value,
  }));
}

export function CheckListField({ fieldSchema, group }: FieldProps) {
  const field = useFormField(fieldSchema, group);
  const options = useMemo<QuestionOption[]>(
    () => normalizeOptions(fieldSchema.options),
    [fieldSchema.options]
  );
  const listType = fieldSchema.listType ?? '';

  const [selectableOptions, setSelectableOptions] = useState<{ value: string; checked: boolean }[]>(
    []
  );

  useEffect(() => {
    const preloadedLookup: Record<string, boolean> = {};
    const answer = String(field.answer ?? '');

    if (answer) {
      answer.split(', ').forEach((item) => {
        preloadedLookup[item] = true;
      });
    }

    setSelectableOptions(
      options.map((option) => ({
        value: option.value,
        checked: preloadedLookup[option.value] === true,
      }))
    );
  }, [options, field.answer]);

  const clickOption = (index: number, checked: boolean) => {
    const updated = [...selectableOptions];
    updated[index] = { ...updated[index], checked };
    setSelectableOptions(updated);

    const selected = updated.filter((o) => o.checked).map((o) => o.value);
    field.setAnswer(selected.join(', '));
  };

  return (
    <FieldsetWrapper {...field} fieldSchema={fieldSchema}>
      <div className={`answer-group checklist-answer-group ${listType}`}>
        {options.map((option, key) => (
          <div className="answer" key={option.value}>
            <input
              type="checkbox"
              checked={selectableOptions[key]?.checked ?? false}
              onChange={(e) => clickOption(key, e.target.checked)}
              name={field.question.id}
              id={`${field.question.id}-${key}`}
              value={option.value}
            />
            <label htmlFor={`${field.question.id}-${key}`}>{option.label}</label>
            {option.supportText && (
              <div
                className="checklist-support-text"
                dangerouslySetInnerHTML={{ __html: option.supportText }}
              />
            )}
          </div>
        ))}
      </div>
    </FieldsetWrapper>
  );
}
